import { inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { from, Observable, Subject } from 'rxjs';
import { filter, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { FormSignatureService } from '@protocols/domain';
import type { IRetrievePipelineDetailQuery, PipelineStepDraftDto, SavePipelineInput } from '@protocols/use-cases';

import { PipelineEditorState, PipelineStepListItem } from '../state/pipeline-editor.state';
import { tapResponse } from '@ngrx/operators';

export const RETRIEVE_PIPELINE_DETAIL_QUERY = new InjectionToken<IRetrievePipelineDetailQuery>(
  'RETRIEVE_PIPELINE_DETAIL_QUERY'
);

/**
 * One-shot result of an editing session. Payload-free on purpose: the draft
 * the consumer needs lives in the shared `PipelineEditorState` as `draft$`,
 * so the event carries only *which action* the user took. Mirrors
 * `PipelineStepEditorOutcome` one level up.
 */
export type PipelineEditorOutcome = { kind: 'saved' } | { kind: 'cancelled' };

@Injectable()
export class PipelineEditorEffects implements OnDestroy {
  private readonly retrievePipelineDetailQuery = inject<IRetrievePipelineDetailQuery>(RETRIEVE_PIPELINE_DETAIL_QUERY);

  /**
   * One-shot save/cancel outcome. Event, not state — never replays to late
   * subscribers, so a reopened dialog can't consume a prior session's result.
   */
  private readonly _outcome$ = new Subject<PipelineEditorOutcome>();
  public readonly outcome$: Observable<PipelineEditorOutcome> = this._outcome$.asObservable();

  constructor(private readonly state: PipelineEditorState) {}

  /**
   * Live canonical signature of the editing draft. Single source for both the
   * facade's `hasChanges$` compare and `captureBaseline`'s snapshot.
   *
   * NOTE: declared above `captureBaseline` on purpose — its initializer reads
   * this property at construction time.
   */
  public readonly currentSignature$: Observable<string> = this.state.select(this.state.draft$, (draft) =>
    FormSignatureService.compute(this.projectSignature(draft))
  );

  /**
   * Snapshots the current draft's signature into `baselineSignature` so the
   * facade's `hasChanges$` can detect divergence afterwards. Triggered at
   * every seed-completion point; no self-gating needed — nothing mutates the
   * draft asynchronously after a seed. Error paths never trigger it: baseline
   * stays `null`, `hasChanges$` stays false, cancel closes silently (correct —
   * nothing was seeded).
   */
  private readonly captureBaseline = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.currentSignature$),
      tap(([, signature]) => this.state.setBaselineSignature(signature))
    )
  );

  /**
   * Seed the editor from a staged draft instead of fetching from the DB. Used
   * by deferred-staging consumers to re-open a pipeline with its staged (not
   * yet persisted) edits intact. Synchronous — no loading/exception phase.
   * Captures the dirty-tracking baseline so pre-existing staged edits do not
   * read as new work.
   */
  public readonly seedFromDraft = this.state.effect((input$: Observable<SavePipelineInput>) =>
    input$.pipe(
      tap((input: SavePipelineInput) => {
        this.state.seedFromDraft(input);
        this.captureBaseline();
      })
    )
  );

  public readonly loadPipeline = this.state.effect((pipelineId$: Observable<string | null>) =>
    pipelineId$.pipe(
      tap((pipelineId) => {
        if (pipelineId === null) {
          this.state.reset();
          this.captureBaseline();
        } else {
          this.state.setLoading(true);
          this.state.setLoadException(null);
        }
      }),
      filter((pipelineId) => pipelineId !== null),
      switchMap((pipelineId) => {
        return from(this.retrievePipelineDetailQuery.execute(pipelineId)).pipe(
          tapResponse(
            (output) => {
              this.state.seedFromPipeline(output);
              this.captureBaseline();
            },
            (error: Error) => this.state.setLoadException(error)
          )
        );
      })
    )
  );

  /**
   * Applies a UI-driven reorder of the steps list. The incoming items must be a
   * permutation of the current `steps$` keyed by `uuid` (the positional index).
   * If an item references a non-existent slot the operation is aborted and an
   * i18n error key is pushed into state — the original list is left untouched so
   * the user can recover.
   */
  public readonly reorderSteps = this.state.effect((items$: Observable<PipelineStepListItem[]>) =>
    items$.pipe(
      withLatestFrom(this.state.steps$),
      tap(([items, currentSteps]: [PipelineStepListItem[], PipelineStepDraftDto[]]) => {
        const reordered: PipelineStepDraftDto[] = [];
        for (const item of items) {
          const draft = currentSteps[parseInt(item.uuid, 10)];
          if (!draft) {
            this.state.setError('pipeline_editor.errors.reorder_failed');
            return;
          }
          reordered.push({ ...draft, order: item.order });
        }
        this.state.setSteps(reordered);
      })
    )
  );

  /** Emit the editing-session outcome as a one-shot event (see `outcome$`). */
  public readonly confirm = this.state.effect<PipelineEditorOutcome>((outcome$: Observable<PipelineEditorOutcome>) =>
    outcome$.pipe(tap((outcome: PipelineEditorOutcome) => this._outcome$.next(outcome)))
  );

  // ============================================================================
  // Signature projection
  // ============================================================================

  /*
   * Canonical fingerprint of everything a cancel would discard. The baseline is
   * captured at seed-completion; the facade recompares on every draft emission
   * and any divergence marks the session dirty. Serialized by
   * `FormSignatureService.compute` (recursive key sort), so key-insertion order
   * never flips the signature.
   *
   * Deliberate scope choices:
   *  - `draft.id` is excluded — identity, not work.
   *  - `editedStepIds` is excluded — re-fetch bookkeeping, not content; this also
   *    keeps "open a child step, save it unchanged" clean (identical draft →
   *    identical projection).
   *  - Steps are fingerprinted in order — reordering is real work.
   *    `deletedStepIds` is sorted (deletion set, not sequence).
   *  - Lang maps drop empty-string entries: the facade's `toFullLangMap`
   *    backfills every language with `''` on the first keystroke, so `{}` and
   *    `{en: ''}` are the same user-visible content.
   */
  private projectSignature(draft: SavePipelineInput): unknown {
    return {
      name: this.normalizeLangMap(draft.name),
      description: this.normalizeLangMap(draft.description),
      env: draft.env,
      steps: draft.steps.map((step) => this.toPlainStep(step)),
      deletedStepIds: [...(draft.deletedStepIds ?? [])].sort(),
    };
  }

  /**
   * Structured drafts carry `PipelineStepCategory` class instances, which the
   * strict signature service rejects — remap them to plain objects. Manual
   * drafts hold an opaque plain-JSON `params` blob and pass through as-is.
   */
  private toPlainStep(step: PipelineStepDraftDto): unknown {
    if (step.mode === 'manual') return { ...step };
    return {
      ...step,
      categories: step.categories.map((category) => ({
        uuid: category.uuid,
        name: { ...category.name },
        options: category.options.map((option) => ({
          uuid: option.uuid,
          name: { ...option.name },
          thrdis: option.thrdis,
        })),
      })),
    };
  }

  /** Drops empty-string entries so `{}` ≡ `{en: ''}`; key sorting is the service's job. */
  private normalizeLangMap(map: { [lang: string]: string } | undefined): { [lang: string]: string } {
    return Object.fromEntries(Object.entries(map ?? {}).filter(([, value]) => value !== ''));
  }

  ngOnDestroy(): void {
    this._outcome$.complete();
  }
}
